import { TableColumn, TableStyles } from 'react-data-table-component';
import { WaSummaryByBranchResult } from 'src/api/wa/store/models/workplace/WaSummary';
import LinkButton from 'src/ui/wards/common/button/LinkButton';

export interface BranchSituation {
  branchName: string;
  branchCode: string;
  aliasBranchCode: string;
  businessLocation: string;
  aliasSalesOfficeName: string;
  salesOfficeCode: string;
  servicedUnits: string;
  respondingUnits: string;
  seismographsInstalled: string;
  targetUnitNumber: string;
  collectedAlarm: string;
  collectionFailed: string;
  seismographOperation: string;
  requiringConfirmation: string;
  unitsConfirmed: string;
  seismographOpScope: string;
  discontinuedMachines: string;
  brokenMachines: string;
  powerOutageNumber: string;
  confinementOccurrences: string;
  rescues: string;
  restoredUnits: string;
  temporaryRestoration: string;
  fullyRestored: string;
  stoppedUnits: string;
  rescueRate: string;
  restoredRate: string;
  fullRecoveryRate: string;
  onCallUnits: string;
  predictedOutage: string;
}

const NUMERIC_COLUMN_WIDTH = '4.5rem';

export const mapSingleBranchToSituation = (
  item: WaSummaryByBranchResult,
  nameOverride?: string
): BranchSituation => {
  const fmt = (val?: number | null) => val?.toLocaleString() ?? '0';
  const pct = (val?: number | null) => val?.toFixed(2) ?? '0.00';

  const adjustBranchName = (item: WaSummaryByBranchResult, override?: string) => {
    if (item.aliasBranchCode === '  ') return '沖日';
    if (override) return override;
    if (item.branchName) return item.branchName;
    return '';
  };

  return {
    branchCode: item.branchCode || '',
    branchName: adjustBranchName(item, nameOverride),
    aliasBranchCode: item.aliasBranchCode || '',
    businessLocation: item.salesOfficeName || '',
    aliasSalesOfficeName: item.aliasSalesOfficeName || '',
    salesOfficeCode: item.salesOfficeCode || '',
    servicedUnits: fmt(item.hosyuCnt),
    respondingUnits: fmt(item.hosyuCnt),
    seismographsInstalled: fmt(item.jishinkeiCnt),
    targetUnitNumber: fmt(item.jidouTaisyouCnt),
    collectedAlarm: fmt(item.herSumiCnt),
    collectionFailed: fmt(item.sssippaiCnt),
    seismographOperation: fmt(item.teishiDaisu1),
    requiringConfirmation: fmt(item.taishougaiCnt),
    unitsConfirmed: fmt(item.taishougaiKakuninCnt),
    seismographOpScope: fmt(item.teishiDaisu2),
    confinementOccurrences: fmt(item.totalTojikomeCnt),
    rescues: fmt(item.tojikomeKyuushustuCnt),
    rescueRate: pct(item.tojikomeKyuushustuRate),
    stoppedUnits: fmt(item.teishiCnt),
    restoredUnits: fmt(item.fukkyuDaisu),
    restoredRate: pct(item.fukkyuRate),
    temporaryRestoration: fmt(item.hdKariFukkyuuCnt),
    fullyRestored: fmt(item.hdHonFukkyuuCnt),
    fullRecoveryRate: pct(item.hdHonFukkyuuRate),
    discontinuedMachines: fmt(item.yousyoriCnt),
    brokenMachines: fmt(item.bussonCnt),
    powerOutageNumber: fmt(item.teidenCnt),
    onCallUnits: fmt(item.oncallTaiouDaisu),
    predictedOutage: fmt(item.teishiYosokuCnt),
  };
};

export const branchSummaryCustomStyles: TableStyles = {
  tableWrapper: {
    style: {
      display: 'block',
      overflow: 'visible',
    },
  },
  table: {
    style: {
      backgroundColor: 'transparent',
      width: '100%',
      tableLayout: 'fixed',
    },
  },
  rows: {
    style: {
      minHeight: '2.25rem',
      borderBottom: '1px solid #d1d5db',
    },
  },
  cells: {
    style: {
      borderRight: '1px solid #d1d5db',
      padding: '0px',
      fontSize: '0.75rem',
    },
  },
};

const rightCol = (
  selector: (row: BranchSituation) => string,
  width = NUMERIC_COLUMN_WIDTH
): TableColumn<BranchSituation> => ({
  selector,
  cell: (row) => {
    const isTotal = row.businessLocation === '';
    return (
      <div className={`flex w-full items-center justify-end pr-2 ${isTotal ? 'my-4' : ''}`}>
        {selector(row)}
      </div>
    );
  },
  width,
});

const percentCol = (
  selector: (row: BranchSituation) => string,
  width = NUMERIC_COLUMN_WIDTH
): TableColumn<BranchSituation> => ({
  selector,
  cell: (row) => <div className="w-full pr-2 text-right">{selector(row)}%</div>,
  width,
});

export const branchSummaryColumns = (
  onBusinessLocationClick: (branchCode: string, salesOfficeCode: string) => void
): TableColumn<BranchSituation>[] => {
  return [
    {
      name: 'Branch',
      selector: (row) => row.branchCode,
      cell: (row) => <div className="w-full text-center font-bold">{row.branchName}</div>,
      width: NUMERIC_COLUMN_WIDTH,
    },
    {
      name: 'Location',
      selector: (row) => row.businessLocation,
      cell: (row) => (
        <LinkButton
          title={row.businessLocation}
          onClick={() => onBusinessLocationClick(row.aliasBranchCode, row.salesOfficeCode)}
          className="w-full justify-center"
        />
      ),
      width: NUMERIC_COLUMN_WIDTH,
    },
    rightCol((row) => row.respondingUnits),
    rightCol((row) => row.seismographsInstalled),
    rightCol((row) => row.targetUnitNumber),
    rightCol((row) => row.collectedAlarm),
    rightCol((row) => row.collectionFailed),
    rightCol((row) => row.seismographOperation),
    rightCol((row) => row.requiringConfirmation),
    rightCol((row) => row.unitsConfirmed),
    rightCol((row) => row.seismographOpScope),
    rightCol((row) => row.confinementOccurrences),
    rightCol((row) => row.rescues),
    percentCol((row) => row.rescueRate),
    rightCol((row) => row.stoppedUnits),
    rightCol((row) => row.restoredUnits),
    percentCol((row) => row.restoredRate),
    rightCol((row) => row.temporaryRestoration),
    rightCol((row) => row.fullyRestored),
    percentCol((row) => row.fullRecoveryRate),
    rightCol((row) => row.discontinuedMachines),
    rightCol((row) => row.brokenMachines),
    rightCol((row) => row.powerOutageNumber),
    rightCol((row) => row.onCallUnits),
    rightCol((row) => row.predictedOutage),
  ];
};
